import { createClient, SDKGatewaysInterface, GatewayClientParams, GatewayMap } from './@common/client';
import { SERVICE_GATEWAYS } from './@common/enums';

export { SERVICE_GATEWAYS };
export type { SERVICE_GATEWAYS as SERVICE_GATEWAYS_TYPE } from './@common/enums';

class SDK {
  client: SDKGatewaysInterface | null = null;

  configure(params: GatewayClientParams) {
    this.client = createClient(params);
  }
}
const sdk = new SDK();

/**
 * Initializes the SDK with the required configuration parameters.
 * Must be called before using {@link gateway}.
 */
export const configure = (params: GatewayClientParams) => {
  sdk.configure(params);
};

/**
 * Returns the gateway instance for the specified service.
 *
 * @typeParam T - A service identifier key from {@link GatewayMap}.
 * @param service - The service identifier (e.g. `SERVICE_GATEWAYS.CUSTOMER_SERVICE`).
 * @returns The corresponding gateway instance with all its methods.
 * @throws {Error} If the SDK has not been configured via {@link configure}.
 * @throws {Error} If an unknown service identifier is provided.
 *
 * @example
 * ```ts
 * configure({ axiosClient, apiToken: '...', services: { customerService: { baseUrl: '...' } } });
 * const customer = gateway('customer-service');
 * const company = await customer.getCompanyById('123');
 * ```
 */
export function gateway<T extends keyof GatewayMap>(service: T): GatewayMap[T];
export function gateway(service: SERVICE_GATEWAYS) {
  if (!sdk.client)
    throw new Error(
      'SDK not configured. Please call configure() with the appropriate parameters before using the SDK.',
    );

  switch (service) {
    case SERVICE_GATEWAYS.CUSTOMER_SERVICE:
      if (!sdk.client.customerService)
        throw new Error(`Service 'customer-service' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.customerService;
    case SERVICE_GATEWAYS.CHAT_CONFIG:
      if (!sdk.client.chatConfig)
        throw new Error(`Service 'chat-config' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.chatConfig;
    case SERVICE_GATEWAYS.CAMPAIGNS:
      if (!sdk.client.campaigns)
        throw new Error(`Service 'campaigns' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.campaigns;
    case SERVICE_GATEWAYS.CHAT_ADAPTER:
      if (!sdk.client.chatAdapter)
        throw new Error(`Service 'chat-adapter' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.chatAdapter;
    case SERVICE_GATEWAYS.ASSETS:
      if (!sdk.client.assets)
        throw new Error(`Service 'assets' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.assets;
    case SERVICE_GATEWAYS.CONTACT_LIST:
      if (!sdk.client.contactList)
        throw new Error(`Service 'contact-list' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.contactList;
    case SERVICE_GATEWAYS.INTEGRATIONS:
      if (!sdk.client.integrations)
        throw new Error(`Service 'integrations' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.integrations;
    case SERVICE_GATEWAYS.CHAT_WEBSERVICE:
      if (!sdk.client.chatWebservice)
        throw new Error(`Service 'chat-webservice' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.chatWebservice;
    case SERVICE_GATEWAYS.CALL_REPORT:
      if (!sdk.client.callReport)
        throw new Error(`Service 'call-report' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.callReport;
    case SERVICE_GATEWAYS.CALLS:
      if (!sdk.client.calls) throw new Error(`Service 'calls' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.calls;
    case SERVICE_GATEWAYS.CASES:
      if (!sdk.client.cases) throw new Error(`Service 'cases' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.cases;
    case SERVICE_GATEWAYS.RH:
      if (!sdk.client.rh) throw new Error(`Service 'rh' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.rh;
    case SERVICE_GATEWAYS.ACCESS_HUB:
      if (!sdk.client.accessHub)
        throw new Error(`Service 'access-hub' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.accessHub;
    case SERVICE_GATEWAYS.NOTIFICATIONS:
      if (!sdk.client.notifications)
        throw new Error(`Service 'notifications' is not configured. Provide its baseUrl in configure().`);
      return sdk.client.notifications;
    default:
      throw new Error(`Unknown service gateway: ${service}`);
  }
}

export * from './campaigns/contracts';
export * from './chat-config/contracts';
export * from './customer-service/contracts';
export * from './chat-adapter/contracts';
export * from './assets/contracts';
export * from './contact-list/contracts';
export * from './integrations/contracts';
export * from './chat-webservice/contracts';
export * from './call-report/contracts';
export * from './calls/contracts';
export * from './cases/contracts';
export * from './rh/contracts';
export * from './access-hub/contracts';
export * from './notifications/contracts';
